// lib/koi-pond-v3/assets/stones.ts
//
// T9.2 — weathered stones at the foreground corners. Compositional
// anchors: they frame the shot, partially in frame, and DO NOT move.
//
// Geometry: a low-detail icosphere whose vertices are pushed in/out by a
// deterministic per-direction noise (duplicated verts at shared corners
// get the SAME displacement → no cracks), then flat-shaded for a faceted
// low-poly rock. Material: matte gray-green with a touch of moss on the
// upward faces and a cool moonlit rim, depth-fogged.

import {
  IcosahedronGeometry,
  ShaderMaterial,
  Mesh,
  Group,
  Color,
  Vector3,
} from 'three';
import { palette } from '../palette';
import { DEPTH_FOG_UNIFORM_DECL, DEPTH_FOG_APPLY_SNIPPET } from '../shaders/depthFog';

type FogUniforms = { uFogColor: { value: Color }; uFogDensity: { value: number } };

// per-direction noise — deterministic so duplicated icosphere verts at a
// shared corner displace identically (no cracks). Returns ~[-1, 1].
function rockNoise(x: number, y: number, z: number, seed: number): number {
  let n = Math.sin(x * 2.7 + seed) * Math.cos(y * 3.1 + seed * 1.3);
  n += 0.6 * Math.sin(y * 4.3 + z * 3.7 + seed * 2.1);
  n += 0.4 * Math.cos(z * 5.1 + x * 4.2 + seed * 0.7);
  return n / 2.0;
}

function buildRockGeometry(seed: number) {
  // detail 2 → 320 faces; non-indexed (PolyhedronGeometry) → flat shading.
  // Raised from detail 1 (80 faces) so the silhouette reads as a weathered
  // rock instead of an obvious low-poly gem. The added subdivision also lets
  // a second, higher-frequency displacement octave carve crags into the form.
  const geo = new IcosahedronGeometry(1, 2);
  const pos = geo.attributes.position;
  const v = new Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.set(pos.getX(i), pos.getY(i), pos.getZ(i)).normalize();
    // two octaves: broad boulder shape + finer crag relief on the silhouette
    const n1 = rockNoise(v.x, v.y, v.z, seed);
    const n2 = rockNoise(v.x * 2.4, v.y * 2.4, v.z * 2.4, seed * 1.7);
    const f = 1 + 0.24 * n1 + 0.1 * n2;
    pos.setXYZ(i, v.x * f, v.y * f, v.z * f);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals(); // flat per-face (non-indexed) → faceted rock
  return geo;
}

function makeStoneMaterial(moonDir: Vector3, fog: FogUniforms): ShaderMaterial {
  return new ShaderMaterial({
    uniforms: {
      uMoonDir: { value: moonDir.clone().normalize() },
      uMoonColor: { value: new Color(palette.moonlight.hex) },
      uBase: { value: new Color(palette.stoneCool.hex) },
      uMoss: { value: new Color(palette.lotusDark.hex) },
      uRim: { value: new Color(palette.rimLight.hex) },
      uFogColor: fog.uFogColor,
      uFogDensity: fog.uFogDensity,
    },
    vertexShader: `
      varying vec3 vWorldPos;
      varying vec3 vNormalW;
      void main() {
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vWorldPos = wp.xyz;
        vNormalW = normalize(mat3(modelMatrix) * normal);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      precision highp float;
      uniform vec3 uMoonDir;
      uniform vec3 uMoonColor;
      uniform vec3 uBase;
      uniform vec3 uMoss;
      uniform vec3 uRim;
      ${DEPTH_FOG_UNIFORM_DECL}
      varying vec3 vWorldPos;
      varying vec3 vNormalW;

      // cheap value noise for weathered surface mottle (more rock detail)
      float hash(vec3 p) {
        return fract(sin(dot(p, vec3(12.9898, 78.233, 37.719))) * 43758.5453);
      }
      float vnoise(vec3 p) {
        vec3 i = floor(p);
        vec3 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        float n000 = hash(i + vec3(0.0, 0.0, 0.0));
        float n100 = hash(i + vec3(1.0, 0.0, 0.0));
        float n010 = hash(i + vec3(0.0, 1.0, 0.0));
        float n110 = hash(i + vec3(1.0, 1.0, 0.0));
        float n001 = hash(i + vec3(0.0, 0.0, 1.0));
        float n101 = hash(i + vec3(1.0, 0.0, 1.0));
        float n011 = hash(i + vec3(0.0, 1.0, 1.0));
        float n111 = hash(i + vec3(1.0, 1.0, 1.0));
        float nx00 = mix(n000, n100, f.x);
        float nx10 = mix(n010, n110, f.x);
        float nx01 = mix(n001, n101, f.x);
        float nx11 = mix(n011, n111, f.x);
        return mix(mix(nx00, nx10, f.y), mix(nx01, nx11, f.y), f.z);
      }

      void main() {
        vec3 N = normalize(vNormalW);
        vec3 V = normalize(cameraPosition - vWorldPos);
        vec3 L = normalize(uMoonDir);

        // Bump mapping — perturb the shading normal by the gradient of the
        // surface noise so the rock catches moonlight across fine crags and
        // pits (a normalMap's detail win without an asset or extra polys).
        float e = 0.18;
        float nC = vnoise(vWorldPos * 4.0);
        vec3 grad = vec3(
          vnoise(vWorldPos * 4.0 + vec3(e, 0.0, 0.0)) - nC,
          vnoise(vWorldPos * 4.0 + vec3(0.0, e, 0.0)) - nC,
          vnoise(vWorldPos * 4.0 + vec3(0.0, 0.0, e)) - nC
        ) / e;
        N = normalize(N - grad * 0.4); // bumpScale
        // wrapped diffuse so faces angled away from the moon still catch
        // some moonlight (lifts the shadowed side + reveals its form)
        float wrap = 0.5;
        float diff = clamp((dot(N, L) + wrap) / (1.0 + wrap), 0.0, 1.0);
        float up = clamp(N.y, 0.0, 1.0);
        vec3 base = mix(uBase, uMoss, up * 0.45);
        // weathered surface mottle — two octaves of world-space value noise
        // give the rocks crags/grain detail instead of flat facets
        float m = vnoise(vWorldPos * 3.5) * 0.62 + vnoise(vWorldPos * 9.5) * 0.38;
        base *= 0.80 + 0.40 * m;
        vec3 color = base * (0.46 + 0.80 * diff) * mix(vec3(1.0), uMoonColor, 0.6);
        // cool moonlit rim along grazing edges
        float fres = pow(1.0 - clamp(dot(N, V), 0.0, 1.0), 2.5);
        color += uRim * fres * 0.72;
        ${DEPTH_FOG_APPLY_SNIPPET}
        gl_FragColor = vec4(color, 1.0);
      }
    `,
  });
}

interface StoneSpec {
  pos: [number, number, number];
  scale: [number, number, number];
  yaw: number;
  seed: number;
}

// Foreground corners (partially in frame), plus one smaller mid-left.
// Camera looks slightly +X, so the right stone sits more in-frame and the
// left ones are cut by the edge — that asymmetry is the framing.
const STONE_SPECS: StoneSpec[] = [
  { pos: [-3.4, 0.9, 4.9], scale: [1.5, 1.1, 1.3], yaw: 0.6, seed: 1.0 }, // foreground-left
  { pos: [3.8, 0.8, 4.3], scale: [1.6, 1.25, 1.25], yaw: -0.8, seed: 2.3 }, // foreground-right
  { pos: [-2.3, 1.05, 3.0], scale: [0.85, 0.7, 0.8], yaw: 1.4, seed: 3.7 }, // mid-left, smaller
];

export function makeStones(moonDir: Vector3, fog: FogUniforms) {
  const root = new Group();
  const mat = makeStoneMaterial(moonDir, fog);
  for (const spec of STONE_SPECS) {
    const mesh = new Mesh(buildRockGeometry(spec.seed), mat);
    mesh.position.set(spec.pos[0], spec.pos[1], spec.pos[2]);
    mesh.scale.set(spec.scale[0], spec.scale[1], spec.scale[2]);
    mesh.rotation.y = spec.yaw;
    root.add(mesh);
  }

  function dispose() {
    mat.dispose();
    root.traverse((o) => {
      if (o instanceof Mesh) o.geometry.dispose();
    });
  }

  return { object: root, dispose };
}
